import { Schema } from "effect";
import {
  AccountId,
  ConversationId,
  ConversationThreadId,
  MessageId,
} from "../../../ids/definitions";
import {
  EmailChannelData,
  MetaCommentChannelData,
  RedoChatChannelData,
  SmsChannelData,
  VoiceChannelData,
} from "./message/channel-data";

export const MESSAGE_CHANNELS = [
  "email",
  "redoChat",
  "facebook",
  "facebookComments",
  "instagram",
  "instagramComments",
  "sms",
  "voice",
  "attentive",
  "postscript",
  "tiktok",
  "contactForm",
] as const;

export const MessageChannel = Schema.Literal(...MESSAGE_CHANNELS);
export type MessageChannel = typeof MessageChannel.Type;

export const MessageOrigin = Schema.Literal("external", "internal");
export type MessageOrigin = typeof MessageOrigin.Type;

export const MessageSenderType = Schema.Literal(
  "merchant",
  "customer",
  "system",
);
export type MessageSenderType = typeof MessageSenderType.Type;

// Base fields shared by all message variants
const BaseMessageFields = {
  accountId: AccountId,
  content: Schema.NullOr(Schema.String),
  conversationThreadId: ConversationThreadId,
  id: MessageId,
  inReplyToId: Schema.NullOr(MessageId),
  origin: MessageOrigin,
  sentAt: Schema.Date,
};

// Channel-specific message variants
const EmailMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("email"),
  channelData: Schema.NullOr(EmailChannelData),
});

const SmsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("sms"),
  channelData: Schema.NullOr(SmsChannelData),
});

const RedoChatMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("redoChat"),
  channelData: Schema.NullOr(RedoChatChannelData),
});

const VoiceMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("voice"),
  channelData: Schema.NullOr(VoiceChannelData),
});

// Meta platform channels share MetaCommentChannelData
const FacebookMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("facebook"),
  channelData: Schema.NullOr(MetaCommentChannelData),
});

const FacebookCommentsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("facebookComments"),
  channelData: Schema.NullOr(MetaCommentChannelData),
});

const InstagramMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("instagram"),
  channelData: Schema.NullOr(MetaCommentChannelData),
});

const InstagramCommentsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("instagramComments"),
  channelData: Schema.NullOr(MetaCommentChannelData),
});

// Channels without specific channelData (always null)
const AttentiveMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("attentive"),
  channelData: Schema.Null,
});

const PostscriptMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("postscript"),
  channelData: Schema.Null,
});

const TiktokMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("tiktok"),
  channelData: Schema.Null,
});

const ContactFormMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("contactForm"),
  channelData: Schema.Null,
});

// Discriminated union on channel
export const MessageResponse = Schema.Union(
  EmailMessage,
  SmsMessage,
  RedoChatMessage,
  VoiceMessage,
  FacebookMessage,
  FacebookCommentsMessage,
  InstagramMessage,
  InstagramCommentsMessage,
  AttentiveMessage,
  PostscriptMessage,
  TiktokMessage,
  ContactFormMessage,
);
export type MessageResponse = typeof MessageResponse.Type;

export const MessageProcessingStatus = Schema.Literal(
  "pending",
  "processing",
  "processed",
  "failed",
);
export type MessageProcessingStatus = typeof MessageProcessingStatus.Type;

export const MessageCursor = Schema.Struct({
  sentAt: Schema.DateFromString,
  id: MessageId,
});
export type MessageCursor = typeof MessageCursor.Type;

export type MessageWithConversationId = MessageResponse & {
  conversationId?: ConversationId;
};
